import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  updateDocument, fetchDocuments, ACCESS_LEVELS, getExpiryStatus,
} from '../../services/docuvaultService';

const {
  FiX, FiUsers, FiShield, FiPlus, FiTrash2, FiCheck, FiClock,
  FiAlertTriangle, FiSearch, FiLock, FiUnlock, FiRefreshCw,
  FiMail, FiUser, FiChevronDown, FiChevronUp, FiSave,
} = FiIcons;

const PARTY_ROLES = [
  { id: 'executor', label: 'Executor', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { id: 'beneficiary', label: 'Beneficiary', color: 'text-green-600 bg-green-50 border-green-200' },
  { id: 'attorney', label: 'Attorney', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { id: 'trustee', label: 'Trustee', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { id: 'co-trustee', label: 'Co-Trustee', color: 'text-pink-600 bg-pink-50 border-pink-200' },
  { id: 'other', label: 'Other', color: 'text-gray-600 bg-gray-50 border-gray-200' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseJsonField(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function getAccessLevelLabel(id) {
  return ACCESS_LEVELS.find(a => a.id === id)?.label || 'Private';
}

// ── Document Delegation Card ────────────────────────────────────────────────

function DelegationDocCard({ doc, onUpdate, allDocs }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [localParties, setLocalParties] = useState([]);
  const [localAccess, setLocalAccess] = useState(doc.access_level || 'private');
  const [localThreshold, setLocalThreshold] = useState(doc.authorization_threshold || 1);
  const [newParty, setNewParty] = useState({ name: '', email: '', role: 'executor' });
  const [hasChanges, setHasChanges] = useState(false);

  const approvals = parseJsonField(doc.authorization_approvals);
  const originalParties = parseJsonField(doc.authorized_parties);

  useEffect(() => {
    setLocalParties(originalParties);
    setLocalAccess(doc.access_level || 'private');
    setLocalThreshold(doc.authorization_threshold || 1);
    setHasChanges(false);
    setSaveError('');
  }, [doc.id]);

  const markChanged = () => setHasChanges(true);

  const addParty = () => {
    if (!newParty.name.trim() || !newParty.email.trim()) return;
    if (localParties.some(p => p.email?.toLowerCase() === newParty.email.trim().toLowerCase())) {
      setSaveError('This email is already an authorized party.');
      return;
    }
    setLocalParties(p => [...p, { ...newParty, id: crypto.randomUUID() }]);
    setNewParty({ name: '', email: '', role: 'executor' });
    setSaveError('');
    markChanged();
  };

  const removeParty = (idx) => {
    setLocalParties(p => p.filter((_, i) => i !== idx));
    markChanged();
  };

  const handleSave = async () => {
    setSaving(true); setSaveError('');
    try {
      const res = await updateDocument(doc.id, {
        access_level: localAccess,
        authorized_parties: JSON.stringify(localParties),
        authorization_threshold: Math.max(1, Math.min(localThreshold, Math.max(localParties.length, 1))),
      });
      if (res?.status === 'success' || res?.id) {
        setHasChanges(false);
        onUpdate?.();
      } else {
        setSaveError(res?.error || 'Failed to save changes.');
      }
    } catch (e) {
      setSaveError(e.message || 'Network error while saving.');
    } finally {
      setSaving(false);
    }
  };

  const isFullyAuthorized = approvals.length >= (doc.authorization_threshold || 1);
  const expiryStatus = getExpiryStatus(doc);
  const roleChip = PARTY_ROLES.find(r => r.id === newParty.role) || PARTY_ROLES[0];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${
        hasChanges ? 'border-accent-gold' : 'border-brand-dark hover:border-accent-navy/20'
      }`}
    >
      {/* Header row */}
      <div className="p-4 flex items-center gap-4">
        <div className="text-2xl">📄</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-accent-navy truncate">{doc.original_name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[9px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-2 py-0.5 rounded-full">
              {getAccessLevelLabel(localAccess)}
            </span>
            <span className="text-[9px] text-text-muted">
              {localParties.length} party{localParties.length !== 1 ? 'ies' : 'y'}
            </span>
            {doc.authorization_required === 1 && (
              <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                isFullyAuthorized ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {isFullyAuthorized ? 'Authorized' : `${approvals.length}/${doc.authorization_threshold || 1}`}
              </span>
            )}
            {expiryStatus !== 'none' && (
              <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                expiryStatus === 'expired' ? 'bg-red-100 text-red-600' :
                expiryStatus === 'critical' ? 'bg-red-50 text-red-500' :
                expiryStatus === 'warning' ? 'bg-amber-50 text-amber-600' :
                'bg-blue-50 text-blue-500'
              }`}>
                {expiryStatus === 'expired' ? 'Expired' :
                 expiryStatus === 'critical' ? 'Expiring ≤3d' :
                 expiryStatus === 'warning' ? 'Expiring ≤14d' : 'Expiring ≤30d'}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-2 bg-accent-gold text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all disabled:opacity-50"
            >
              <SafeIcon icon={saving ? FiRefreshCw : FiSave} className={`w-3.5 h-3.5 ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-2 text-text-muted hover:text-accent-navy rounded-xl border border-brand-dark hover:border-accent-navy transition-all"
          >
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Save error */}
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="px-4 pb-3"
          >
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl text-[10px] text-red-700">
              <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {saveError}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded management panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="border-t border-brand-light overflow-hidden"
          >
            <div className="p-4 space-y-5">

              {/* Access Level */}
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                  Access Level
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {ACCESS_LEVELS.map(al => (
                    <button
                      key={al.id}
                      onClick={() => { setLocalAccess(al.id); markChanged(); }}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        localAccess === al.id
                          ? 'border-accent-gold bg-accent-gold/5'
                          : 'border-brand-dark hover:border-accent-gold/50'
                      }`}
                    >
                      <p className="text-[10px] font-bold text-accent-navy">{al.label}</p>
                      <p className="text-[9px] text-text-muted font-light leading-snug mt-0.5">{al.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Multi-party threshold */}
              {localAccess === 'multi_party' && (
                <div className="p-3 bg-purple-50 border border-purple-100 rounded-xl">
                  <label className="text-[9px] font-black uppercase tracking-widest text-purple-800 block mb-1.5">
                    Required Approvals
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      max={Math.max(localParties.length, 1)}
                      value={localThreshold}
                      onChange={e => { setLocalThreshold(parseInt(e.target.value) || 1); markChanged(); }}
                      className="w-20 px-3 py-2 bg-white border-2 border-purple-200 rounded-xl outline-none focus:border-purple-400 text-sm text-center"
                    />
                    <p className="text-[10px] text-purple-700">
                      of <strong>{localParties.length}</strong> parties must approve
                    </p>
                  </div>
                </div>
              )}

              {/* Authorized Parties List */}
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-accent-navy block mb-2">
                  Authorized Parties
                </label>
                {localParties.length === 0 ? (
                  <div className="p-4 bg-brand-light rounded-xl border border-brand-dark border-dashed text-center">
                    <p className="text-[10px] text-text-muted font-light">No authorized parties yet.</p>
                    <p className="text-[9px] text-text-muted/70 mt-0.5">Add parties below to delegate access.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {localParties.map((party, idx) => {
                      const approved = approvals.find(
                        a => a.email?.toLowerCase() === party.email?.toLowerCase()
                      );
                      const roleChip = PARTY_ROLES.find(r => r.id === party.role) || PARTY_ROLES[PARTY_ROLES.length - 1];
                      return (
                        <div
                          key={party.id || idx}
                          className="flex items-center justify-between p-3 bg-brand-light rounded-xl border border-brand-dark"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-brand-dark flex-shrink-0">
                              <SafeIcon icon={FiUser} className="w-4 h-4 text-text-muted" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-bold text-accent-navy truncate">{party.name}</p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-[9px] text-text-muted flex items-center gap-1">
                                  <SafeIcon icon={FiMail} className="w-3 h-3" />
                                  {party.email}
                                </span>
                                <span className={`text-[8px] px-1.5 py-0.5 rounded-md font-black uppercase tracking-wider border ${roleChip.color}`}>
                                  {roleChip.label}
                                </span>
                                {approved ? (
                                  <span className="flex items-center gap-1 text-[8px] font-black text-green-600">
                                    <SafeIcon icon={FiCheck} className="w-3 h-3" /> Approved
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-[8px] font-light text-text-muted">
                                    <SafeIcon icon={FiClock} className="w-3 h-3" /> Pending
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeParty(idx)}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                            title="Remove access"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add new party */}
              <div className="p-4 bg-white border-2 border-dashed border-accent-gold/30 rounded-2xl space-y-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold flex items-center gap-1.5">
                  <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" /> Add Authorized Party
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input
                    value={newParty.name}
                    onChange={e => setNewParty(p => ({ ...p, name: e.target.value }))}
                    placeholder="Full Legal Name"
                    className="px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                  />
                  <input
                    value={newParty.email}
                    onChange={e => setNewParty(p => ({ ...p, email: e.target.value }))}
                    placeholder="Email Address"
                    type="email"
                    className="px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newParty.role}
                    onChange={e => setNewParty(p => ({ ...p, role: e.target.value }))}
                    className="flex-1 px-3 py-2.5 bg-brand-light border-2 border-brand-dark rounded-xl outline-none focus:border-accent-gold text-xs"
                  >
                    {PARTY_ROLES.map(r => (
                      <option key={r.id} value={r.id}>{r.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={addParty}
                    disabled={!newParty.name.trim() || !newParty.email.trim()}
                    className="px-4 py-2.5 bg-accent-navy text-white rounded-xl text-xs font-black flex items-center gap-1.5 hover:bg-accent-navy/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

const DocuVaultAccessDelegationModal = ({ isOpen, onClose, onUpdate }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [filterAccess, setFilterAccess] = useState('all');

  const loadDocs = async () => {
    setLoading(true);
    try {
      const data = await fetchDocuments({ category: 'all' });
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      setDocs([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) loadDocs();
  }, [isOpen]);

  const filteredDocs = useMemo(() => {
    let result = docs;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      result = result.filter(d =>
        d.original_name?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      );
    }
    if (filterAccess !== 'all') {
      result = result.filter(d => d.access_level === filterAccess);
    }
    return result;
  }, [docs, searchQ, filterAccess]);

  const stats = useMemo(() => {
    const totalParties = docs.reduce((acc, d) => acc + parseJsonField(d.authorized_parties).length, 0);
    const delegatedCount = docs.filter(d => d.access_level !== 'private').length;
    const pendingApprovals = docs.reduce((acc, d) => {
      const approvals = parseJsonField(d.authorization_approvals).length;
      const threshold = d.authorization_threshold || 1;
      return acc + (d.authorization_required === 1 && approvals < threshold ? 1 : 0);
    }, 0);
    return { totalParties, delegatedCount, pendingApprovals, totalDocs: docs.length };
  }, [docs]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-3xl overflow-hidden border border-brand-dark flex flex-col max-h-[94vh]"
      >
        {/* Header */}
        <div className="p-7 bg-gradient-to-r from-accent-navy via-[#1a2a5e] to-purple-900 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiUsers} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">Access Delegation</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                Manage who can access your vault documents
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white rounded-xl transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-4 divide-x divide-brand-light border-b border-brand-light flex-shrink-0">
          {[
            { label: 'Documents', value: stats.totalDocs, icon: FiShield, color: 'text-blue-500' },
            { label: 'Delegated', value: stats.delegatedCount, icon: FiUnlock, color: 'text-accent-gold' },
            { label: 'Parties', value: stats.totalParties, icon: FiUsers, color: 'text-purple-500' },
            { label: 'Pending Auth', value: stats.pendingApprovals, icon: FiClock, color: 'text-amber-500' },
          ].map(s => (
            <div key={s.label} className="p-4 text-center">
              <SafeIcon icon={s.icon} className={`w-5 h-5 ${s.color} mx-auto mb-1`} />
              <p className="text-lg font-serif font-bold text-accent-navy">{s.value}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="p-5 border-b border-brand-light flex items-center gap-3 flex-wrap flex-shrink-0 bg-brand-light/30">
          <div className="relative flex-1 min-w-[200px]">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search documents…"
              className="w-full pl-11 pr-4 py-2.5 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
            />
          </div>
          <select
            value={filterAccess}
            onChange={e => setFilterAccess(e.target.value)}
            className="px-4 py-2.5 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-xs font-bold"
          >
            <option value="all">All Access Levels</option>
            {ACCESS_LEVELS.map(a => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
          <button
            onClick={loadDocs}
            className="p-2.5 bg-white border-2 border-brand-dark rounded-xl text-text-muted hover:text-accent-navy hover:border-accent-navy transition-all"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Document list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
              <p className="text-sm text-text-muted font-light">Loading delegation data…</p>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 bg-brand-light rounded-3xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiUsers} className="w-10 h-10 text-text-muted/30" />
              </div>
              <h4 className="text-xl font-serif text-accent-navy mb-2">
                {searchQ || filterAccess !== 'all' ? 'No matching documents' : 'No documents to delegate'}
              </h4>
              <p className="text-sm text-text-muted font-light max-w-xs">
                {searchQ || filterAccess !== 'all'
                  ? 'Try adjusting your search or filter.'
                  : 'Upload documents to the vault first, then return here to manage access.'}
              </p>
            </div>
          ) : (
            filteredDocs.map(doc => (
              <DelegationDocCard
                key={doc.id}
                doc={doc}
                onUpdate={() => { loadDocs(); onUpdate?.(); }}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-light bg-brand-light/50 flex items-center justify-between flex-shrink-0">
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
            Changes are saved per-document. Unsaved changes show a gold border.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default DocuVaultAccessDelegationModal;